import { useState, useEffect } from 'react'
import { apiClient } from '../services/api'
import { useAuthStore } from '../services/authStore'
import queueService from '../services/queueService'
import { useTranslation } from '../contexts/TranslationContext'
import Card from '../components/ui/Card'
import LoadingState from '../components/ui/LoadingState'
import PageShell from '../components/ui/PageShell'
import { ui } from '../styles/ui'
import { cn } from '../utils/cn'
import LLMSettingsContent from './llm-settings/LLMSettingsContent'
import { QUEUE_KEYS, QUEUE_PROMPT_FIELDS, buildQueueState } from './llm-settings/queueConfig'

const LLMSettings = () => {
  const { t } = useTranslation()

  const [llmEngines, setLlmEngines] = useState([])
  const [queueEngines, setQueueEngines] = useState({
    fast: { primary: '', fallback: '' },
    slow: { primary: '', fallback: '' },
    translate: { primary: '', fallback: '' },
    qrcode: { primary: '', fallback: '' },
    speech: { primary: '', fallback: '' },
  })
  const [queueProcessing, setQueueProcessing] = useState(buildQueueState(true))
  const [queueToggleLoading, setQueueToggleLoading] = useState(buildQueueState(false))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [enginesSaved, setEnginesSaved] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const parseJson = (value) => {
    if (Array.isArray(value)) return value
    if (typeof value !== 'string') return []
    try {
      return JSON.parse(value)
    } catch {
      return []
    }
  }

  const normalizeSettingValue = (value) => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    }
    return value
  }

  const loadSettings = async () => {
    setLoading(true)
    setMessage('')
    try {
      const [settingsResponse, queueStatus] = await Promise.all([
        apiClient.get('/llm-settings'),
        queueService.status().catch(() => null),
      ])
      const response = settingsResponse
      const settings = response.data.settings || []
      const enginesSetting = settings.find((item) => item.key === 'llm_engines')
      const engines = enginesSetting ? parseJson(normalizeSettingValue(enginesSetting.value)) : []
      const primary = {
        fast: settings.find((item) => item.key === 'llm_primary_engine_fast')?.value || '',
        slow: settings.find((item) => item.key === 'llm_primary_engine_slow')?.value || '',
        translate: settings.find((item) => item.key === 'llm_primary_engine_translate')?.value || '',
        qrcode: settings.find((item) => item.key === 'llm_primary_engine_qrcode')?.value || '',
        speech: settings.find((item) => item.key === 'llm_primary_engine_speech')?.value || '',
      }
      const fallback = {
        fast: settings.find((item) => item.key === 'llm_fallback_engine_fast')?.value || '',
        slow: settings.find((item) => item.key === 'llm_fallback_engine_slow')?.value || '',
        translate: settings.find((item) => item.key === 'llm_fallback_engine_translate')?.value || '',
        qrcode: settings.find((item) => item.key === 'llm_fallback_engine_qrcode')?.value || '',
        speech: settings.find((item) => item.key === 'llm_fallback_engine_speech')?.value || '',
      }
      setLlmEngines(Array.isArray(engines) ? engines : [])
      setEnginesSaved(true)
      setQueueEngines({
        fast: { primary: primary.fast, fallback: fallback.fast },
        slow: { primary: primary.slow, fallback: fallback.slow },
        translate: { primary: primary.translate, fallback: fallback.translate },
        qrcode: { primary: primary.qrcode, fallback: fallback.qrcode },
        speech: { primary: primary.speech, fallback: fallback.speech },
      })
      const queueFlags = queueStatus?.queue_processing
      const normalizedQueueState = QUEUE_KEYS.reduce((acc, queue) => {
        const value = queueFlags?.[queue.key]
        acc[queue.key] = typeof value === 'boolean' ? value : true
        return acc
      }, {})
      setQueueProcessing(normalizedQueueState)
    } catch (error) {
      setMessage('Failed to load queue engine settings')
    } finally {
      setLoading(false)
    }
  }

  const updateQueueEngine = (queue, field, value) => {
    setQueueEngines((prev) => ({
      ...prev,
      [queue]: {
        ...prev[queue],
        [field]: value,
      },
    }))
  }

  const updateEnginePrompt = (queueKey, value) => {
    const selectedEngineId = queueEngines[queueKey]?.primary
    if (!selectedEngineId) return
    const promptField = QUEUE_PROMPT_FIELDS[queueKey]?.field
    if (!promptField) return

    setEnginesSaved(false)
    setLlmEngines((prev) =>
      prev.map((engine) =>
        engine.id === selectedEngineId
          ? { ...engine, [promptField]: value }
          : engine
      )
    )
  }

  const selectedPrimaryEngine = (queueKey) =>
    llmEngines.find((engine) => engine.id === queueEngines[queueKey]?.primary)

  const queuePromptValue = (queueKey) => {
    const promptField = QUEUE_PROMPT_FIELDS[queueKey]?.field
    if (!promptField) return ''
    const engine = selectedPrimaryEngine(queueKey)
    return engine?.[promptField] || ''
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage('')
    try {
      const payloadSettings = [
        {
          key: 'llm_engines',
          value: llmEngines,
          type: 'json',
          description: 'List of configured LLM engines',
        },
        {
          key: 'llm_primary_engine_fast',
          value: queueEngines.fast.primary,
          type: 'string',
          description: 'Primary LLM engine for the fast queue',
        },
        {
          key: 'llm_fallback_engine_fast',
          value: queueEngines.fast.fallback,
          type: 'string',
          description: 'Fallback LLM engine for the fast queue',
        },
        {
          key: 'llm_primary_engine_slow',
          value: queueEngines.slow.primary,
          type: 'string',
          description: 'Primary LLM engine for the slow queue',
        },
        {
          key: 'llm_fallback_engine_slow',
          value: queueEngines.slow.fallback,
          type: 'string',
          description: 'Fallback LLM engine for the slow queue',
        },
        {
          key: 'llm_primary_engine_translate',
          value: queueEngines.translate.primary,
          type: 'string',
          description: 'Primary LLM engine for the translate queue',
        },
        {
          key: 'llm_fallback_engine_translate',
          value: queueEngines.translate.fallback,
          type: 'string',
          description: 'Fallback LLM engine for the translate queue',
        },
        {
          key: 'llm_primary_engine_qrcode',
          value: queueEngines.qrcode.primary,
          type: 'string',
          description: 'Primary LLM engine for the QR code queue',
        },
        {
          key: 'llm_fallback_engine_qrcode',
          value: queueEngines.qrcode.fallback,
          type: 'string',
          description: 'Fallback LLM engine for the QR code queue',
        },
        {
          key: 'llm_primary_engine_speech',
          value: queueEngines.speech.primary,
          type: 'string',
          description: 'Primary LLM engine for the speech queue',
        },
        {
          key: 'llm_fallback_engine_speech',
          value: queueEngines.speech.fallback,
          type: 'string',
          description: 'Fallback LLM engine for the speech queue',
        },
      ]

      await apiClient.put('/llm-settings', { settings: payloadSettings })
      setEnginesSaved(true)
      setMessage('Queue-specific engine settings saved successfully')
    } catch (error) {
      setMessage(error.message || 'Failed to save queue engine settings')
    } finally {
      setSaving(false)
    }
  }

  const toggleQueueProcessing = async (queueKey) => {
    const nextState = !queueProcessing[queueKey]
    setQueueToggleLoading((prev) => ({
      ...prev,
      [queueKey]: true,
    }))
    setMessage('')
    try {
      const status = await queueService.setQueueEnabled(queueKey, nextState)
      const queueFlags = status?.queue_processing
      if (queueFlags && typeof queueFlags === 'object') {
        const normalizedQueueState = QUEUE_KEYS.reduce((acc, queue) => {
          const value = queueFlags?.[queue.key]
          acc[queue.key] = typeof value === 'boolean' ? value : true
          return acc
        }, {})
        setQueueProcessing(normalizedQueueState)
      } else {
        setQueueProcessing((prev) => ({
          ...prev,
          [queueKey]: nextState,
        }))
      }
      setMessage(`${t('Queue', 'Queue')} '${queueKey}' ${nextState ? t('enabled', 'enabled') : t('disabled', 'disabled')}`)
    } catch (error) {
      setMessage(`${t('Failed to update queue', 'Failed to update queue')} '${queueKey}'`)
    } finally {
      setQueueToggleLoading((prev) => ({
        ...prev,
        [queueKey]: false,
      }))
    }
  }

  const { user } = useAuthStore()

  if (!user?.is_admin) {
    return (
      <PageShell>
        <Card>
          <div className={ui.cardBody}>
            <h3 className={ui.title}>{t('Access Denied', 'Access Denied')}</h3>
            <p className={cn(ui.subtitle, 'mt-2')}>{t('You are not authorised to view LLM settings.', 'You are not authorised to view LLM settings.')}</p>
          </div>
        </Card>
      </PageShell>
    )
  }

  if (loading) {
    return (
      <PageShell><LoadingState /></PageShell>
    )
  }

  return (
    <PageShell>
      <LLMSettingsContent t={t} message={message} engines={llmEngines} queueEngines={queueEngines}
        queueProcessing={queueProcessing} queueToggleLoading={queueToggleLoading} enginesSaved={enginesSaved}
        saving={saving} queuePromptValue={queuePromptValue} onEnginesChange={(engines) => { setEnginesSaved(false); setLlmEngines(engines) }}
        onQueueEngineChange={updateQueueEngine} onPromptChange={updateEnginePrompt} onToggle={toggleQueueProcessing} onSave={handleSave} />
    </PageShell>
  )
}

export default LLMSettings
